import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'

export interface FollowUpStep {
  id: string
  configId: string
  position: number
  mode: 'TEXT' | 'AI'
  content: string | null
  metadata: Record<string, unknown> | null
  delay_minutes: number
}

export type FollowUpScope = 'AI_ATTENDANCES' | 'HUMAN_ATTENDANCES' | 'ALL'

export type FollowUpInactivityType =
  | 'WAITING_CONTACT_REPLY'
  | 'WAITING_TEAM_REPLY'

export interface FollowUpConfig {
  id: string
  organizationId: string
  agentId: string | null
  name: string
  scope: FollowUpScope
  inactivityType: FollowUpInactivityType
  inactivity_minutes: number
  stop_on_reply: boolean
  stopOnStages: string[]
  stopOnTagIds: string[]
  isActive: boolean
  createdAt: string
  steps: FollowUpStep[]
}

export interface FollowUpConfigInput {
  name: string
  agentId?: string | null
  scope?: FollowUpScope
  inactivityType?: FollowUpInactivityType
  inactivity_minutes: number
  stop_on_reply: boolean
  stopOnStages?: string[]
  stopOnTagIds?: string[]
  isActive: boolean
}

export interface FollowUpStepInput {
  position: number
  mode: 'TEXT' | 'AI'
  content: string
  metadata?: Record<string, unknown>
  delay_minutes: number
}

const followUpKeys = {
  list: (orgId: string | null, agentId?: string) =>
    ['follow-up-configs', orgId, agentId] as const,
  detail: (orgId: string | null, id: string) =>
    ['follow-up-configs', orgId, id] as const,
}

export function useFollowUpConfigs(orgId: string | null) {
  return useQuery<FollowUpConfig[]>({
    queryKey: ['follow-up-configs', orgId],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<FollowUpConfig>>(
        `/orgs/${orgId}/follow-up-configs`,
        { params: { limit: 100 } }
      )
      return res.data.data
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useFollowUpConfig(
  orgId: string | null,
  agentId: string | null
) {
  return useQuery<FollowUpConfig | null>({
    queryKey: followUpKeys.list(orgId, agentId ?? undefined),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<FollowUpConfig>>(
        `/orgs/${orgId}/follow-up-configs`,
        { params: { agentId: agentId ?? '' } }
      )
      return res.data.data[0] ?? null
    },
    enabled: !!orgId && !!agentId,
    staleTime: 30_000,
  })
}

export function useCreateFollowUpConfig(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: FollowUpConfigInput) => {
      const res = await api.post<FollowUpConfig>(
        `/orgs/${orgId}/follow-up-configs`,
        body
      )
      return res.data
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: followUpKeys.list(orgId, data.agentId ?? undefined),
      })
    },
  })
}

export function useUpdateFollowUpConfig(
  orgId: string | null,
  configId: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: Partial<FollowUpConfigInput>) => {
      const res = await api.put<FollowUpConfig>(
        `/orgs/${orgId}/follow-up-configs/${configId}`,
        body
      )
      return res.data
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: followUpKeys.list(orgId, data.agentId ?? undefined),
      })
    },
  })
}

export function useAddFollowUpStep(orgId: string | null, configId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: FollowUpStepInput) => {
      const res = await api.post<FollowUpStep>(
        `/orgs/${orgId}/follow-up-configs/${configId}/steps`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['follow-up-configs', orgId] })
    },
  })
}

export function useUpdateFollowUpStep(
  orgId: string | null,
  configId: string,
  stepId: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: Partial<FollowUpStepInput>) => {
      const res = await api.put<FollowUpStep>(
        `/orgs/${orgId}/follow-up-configs/${configId}/steps/${stepId}`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['follow-up-configs', orgId] })
    },
  })
}

export function useRemoveFollowUpStep(orgId: string | null, configId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (stepId: string) => {
      await api.delete(
        `/orgs/${orgId}/follow-up-configs/${configId}/steps/${stepId}`
      )
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['follow-up-configs', orgId] })
    },
  })
}
